import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash.debounce';
import { Checkbox } from '../../../input';

import { BENCHMARKS_SELECT } from './constants';
import { hasSelected } from '../../helper';
import { IBenchmarkProps } from './interface';
import Button from '@core/design-system/button';
import { HiOutlineChevronUp } from '@react-icons/all-files/hi/HiOutlineChevronUp';
import { HiOutlineChevronDown } from '@react-icons/all-files/hi/HiOutlineChevronDown';
import { HiPencil } from '@react-icons/all-files/hi/HiPencil';
import { PrimeTooltip } from '../../../prime-tooltip';
import { PRIME_TRACKING_ROUTES } from '../../../constants';
import {
  IconButton,
  List,
  ListItemButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { FiPlus } from '@react-icons/all-files/fi/FiPlus';

export const Benchmark = memo<IBenchmarkProps>(function Benchmark({
  defaultSelectedBenchmarks = [],
  onSelectedBenchmarksChange,
  userIsPrime = true,
  customBenchmarks,
  onUpdateCustomBenchmark = () => {},
  customBenchmarksEnabled = false,
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedBenchmarks, setSelectedBenchmarks] = useState(
    defaultSelectedBenchmarks
  );

  useEffect(() => {
    if (defaultSelectedBenchmarks && defaultSelectedBenchmarks?.length) {
      setSelectedBenchmarks(defaultSelectedBenchmarks);
    }
  }, [defaultSelectedBenchmarks]);

  const delayed = useCallback(debounce(onSelectedBenchmarksChange, 500), [
    onSelectedBenchmarksChange,
  ]);

  useEffect(() => {
    return delayed.cancel;
  }, [delayed]);

  const onSelectBenchmark = (benchmark: string) => {
    const exist = hasSelected(benchmark, selectedBenchmarks);

    if (exist) {
      setSelectedBenchmarks([...selectedBenchmarks, benchmark]);

      delayed([...selectedBenchmarks, benchmark]);
    } else {
      const filtered = selectedBenchmarks.filter((b) => b !== benchmark);

      setSelectedBenchmarks(filtered);
      delayed(filtered);
    }
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const filteredBenchmarks = useMemo(() => {
    const availableBenchmarks = ['cdi', 'ibov'];

    return BENCHMARKS_SELECT.map(({ value, label }) => ({
      value,
      label,
      disabled: !userIsPrime && !availableBenchmarks.includes(value),
    }));
  }, [BENCHMARKS_SELECT, userIsPrime]);

  return (
    <>
      <Button
        data-testid="benchmark-button"
        variant="outlined"
        size="small"
        id="indicator"
        color="inherit"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        sx={{
          minWidth: 140,
          fontWeight: 500,
          height: 32,
        }}
        endIcon={open ? <HiOutlineChevronUp /> : <HiOutlineChevronDown />}
      >
        Indicadores
      </Button>

      <Popover
        id="benchmark-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 255,
            height: 400,
          },
        }}
      >
        <Stack p={2} gap={1.5} height="100%">
          <Typography>Indicadores</Typography>
          {!!customBenchmarks?.length ? (
            <List>
              {customBenchmarks?.map((benchmark) => (
                <ListItemButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectBenchmark(benchmark.value);
                  }}
                  sx={{
                    p: 0,
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Checkbox
                      sx={{
                        padding: 0,
                      }}
                      size="small"
                      checked={
                        !hasSelected(benchmark.value, selectedBenchmarks)
                      }
                    />
                    <Typography color="primary">{benchmark.label}</Typography>
                  </Stack>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdateCustomBenchmark();
                      handleClose();
                    }}
                  >
                    <HiPencil size={16} />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
          ) : (
            <>
              {customBenchmarksEnabled && (
                <Button
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                  startIcon={<FiPlus size={16} />}
                  onClick={(e) => {
                    onUpdateCustomBenchmark();
                    handleClose();
                  }}
                >
                  Criar benchmark da carteira
                </Button>
              )}
            </>
          )}

          <Stack flexGrow={1} sx={{ overflowY: 'auto' }}>
            <List>
              {filteredBenchmarks.map(({ value, label, disabled }) => (
                <PrimeTooltip
                  userIsPrime={!disabled}
                  link={PRIME_TRACKING_ROUTES.CTAPortfolioBenchmarks}
                >
                  <ListItemButton
                    key={`benchmark-${value}`}
                    sx={{
                      px: 0,
                      justifyContent: 'space-between',
                    }}
                    onClick={() => (disabled ? {} : onSelectBenchmark(value))}
                    disabled={disabled}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Checkbox
                        sx={{
                          padding: 0,
                        }}
                        size="small"
                        checked={!hasSelected(value, selectedBenchmarks)}
                      />
                      <Typography>{label}</Typography>
                    </Stack>
                  </ListItemButton>
                </PrimeTooltip>
              ))}
            </List>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
});
